/**
 * GuestProjectPage
 * Main page for external contractor access to projects
 * Mobile-first design for on-site use
 */
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Card, Alert, Spinner } from 'react-bootstrap';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import logoWhite from '../../Images/logo_white.png';
import { useGuestProject } from './hooks/useGuestProject';
import { ProjectViewData } from '../../types/guestAccess';
import GuestBuildingHeader from './components/GuestBuildingHeader';
import GuestDelmomentSection from './components/GuestDelmomentSection';
import GuestNotesSection from './components/GuestNotesSection';
import GuestChecklistsSection from './components/GuestChecklistsSection';
import GuestPhotosSection from './components/GuestPhotosSection';
import GuestAttachmentsSection from './components/GuestAttachmentsSection';
import GuestCompletionSection from './components/GuestCompletionSection';
import { GuestLinkRefusalPage } from './components/GuestLinkStatePages';
import './GuestProjectPage.css';

interface GuestProjectPageProps {
    /** The personal link token from the URL */
    token: string;
    projectKey: string;
    /** Pre-loaded project data from GuestProjectRoute */
    initialData: ProjectViewData;
}

const GuestProjectPage: React.FC<GuestProjectPageProps> = ({ token, projectKey, initialData }) => {
    const {
        loading,
        error,
        refusal,
        project,
        building,
        externalUserId,
        submitting,
        uploadingPhotoType,
        completedAt,
        completedNote,
        clearError,
        setCompletion,
        addNote,
        updateNote,
        deleteNote,
        createChecklist,
        addChecklistItem,
        setChecklistItemCompleted,
        deleteChecklistItem,
        deleteChecklist,
        uploadPhoto,
        deletePhoto,
        uploadAttachment,
        deleteAttachment,
        getDownloadUrl,
        canDelete,
    } = useGuestProject(token, initialData, { projectKey });

    // Utförd: read-only until the guest undoes their own completion signal,
    // which reopens the project (the server allows only that write).
    const [closed, setClosed] = useState(initialData.state === 'completed');
    const handleSetCompletion = useCallback(async (completed: boolean, note?: string) => {
        await setCompletion(completed, note);
        if (!completed) setClosed(false);
    }, [setCompletion]);

    if (refusal) {
        return <GuestLinkRefusalPage token={token} refusal={refusal} />;
    }

    // Loading state
    if (loading) {
        return (
            <div className="guest-project-page guest-project-page--loading">
                <div className="guest-project-page__header">
                    <img src={logoWhite} alt="Molnfastigheter" className="guest-project-page__header-logo" />
                </div>
                <div className="guest-project-page__centered">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </Spinner>
                </div>
            </div>
        );
    }

    // Error state - only show full error page if project failed to load initially
    if (!project || !building || !externalUserId) {
        return (
            <div className="guest-project-page guest-project-page--error">
                <div className="guest-project-page__header">
                    <img src={logoWhite} alt="Molnfastigheter" className="guest-project-page__header-logo" />
                </div>
                <div className="guest-project-page__centered">
                    <Card className="text-center">
                        <Card.Body>
                            <AlertCircle size={48} className="text-danger mb-3" />
                            <h5>Kunde inte ladda projektet</h5>
                            <p className="text-muted mb-0">
                                {error || 'Länken är ogiltig eller har upphört att gälla.'}
                            </p>
                        </Card.Body>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="guest-project-page">
            <div className="guest-project-page__header">
                <img src={logoWhite} alt="Molnfastigheter" className="guest-project-page__header-logo" />
            </div>
            <Container className="guest-project-page__container">
                <Link to={`/guest/${token}`} className="guest-page__back d-inline-flex align-items-center gap-1 mb-3">
                    <ArrowLeft size={16} /> Alla mina uppgifter
                </Link>

                {/* Building and Project Info */}
                <GuestBuildingHeader
                    building={building}
                    projectTitle={project.title}
                />

                {/* Error alert */}
                {error && (
                    <Alert variant="danger" onClose={clearError} dismissible>
                        {error}
                    </Alert>
                )}

                {/* Linked work orders (delmoment) — reachable via this same token */}
                <GuestDelmomentSection token={token} projectKey={projectKey} workOrders={initialData.workOrders} />

                {/* Notes Section */}
                <GuestNotesSection
                    notes={project.notes}
                    externalUserId={externalUserId}
                    onAddNote={addNote}
                    onUpdateNote={updateNote}
                    onDeleteNote={deleteNote}
                    submitting={submitting}
                    canDelete={canDelete}
                    readOnly={closed}
                />

                {/* Checklists Section */}
                <GuestChecklistsSection
                    checklists={project.checklists}
                    externalUserId={externalUserId}
                    onCreateChecklist={createChecklist}
                    onAddChecklistItem={addChecklistItem}
                    onSetItemCompleted={setChecklistItemCompleted}
                    onDeleteChecklistItem={deleteChecklistItem}
                    onDeleteChecklist={deleteChecklist}
                    submitting={submitting}
                    canDelete={canDelete}
                    readOnly={closed}
                />

                {/* Photos Section */}
                <GuestPhotosSection
                    beforePhotos={project.beforePictures || {}}
                    progressPhotos={project.progressPictures || {}}
                    afterPhotos={project.afterPictures || {}}
                    externalUserId={externalUserId}
                    onUploadPhoto={uploadPhoto}
                    onDeletePhoto={deletePhoto}
                    onGetDownloadUrl={getDownloadUrl}
                    submitting={submitting}
                    uploadingPhotoType={uploadingPhotoType}
                    canDelete={canDelete}
                    readOnly={closed}
                />

                {/* Attachments Section */}
                <GuestAttachmentsSection
                    attachments={project.attachments}
                    externalUserId={externalUserId}
                    onUploadAttachment={uploadAttachment}
                    onDeleteAttachment={deleteAttachment}
                    onGetDownloadUrl={getDownloadUrl}
                    submitting={submitting}
                    canDelete={canDelete}
                    readOnly={closed}
                />

                {/* Completion Section */}
                <GuestCompletionSection
                    completedAt={completedAt}
                    completedNote={completedNote}
                    onSetCompletion={handleSetCompletion}
                    readOnly={closed}
                    submitting={submitting}
                    openWorkOrderCount={
                        initialData.workOrders.filter((wo) => wo.status === 'open').length
                    }
                />

                {/* Footer */}
                <div className="guest-project-page__footer">
                    <small className="text-muted">
                        Powered by Molnfastigheter
                    </small>
                </div>
            </Container>
        </div>
    );
};

export default GuestProjectPage;
